import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tubo/ui/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";
import {
  useQuantityKindList,
  useQuantityKindCreate,
  useQuantityKindDelete,
  usePublishQuantityKind,
  getQuantityKindListQueryKey,
} from "@tubo/api-models/hooks/quantity-kind-api.hooks";
import { useSemanticNamespaceList } from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import type { QuantityKindListItemVO, UnitDefinitionVO } from "@tubo/api-models/hooks/quantity-kind-api.schemas";
import { Badge } from "@tubo/ui/components/ui/badge";
import { UnitConversionFields } from "@client/components/semantic-platform/quantities/unit-conversion-fields";
import { NS_CONTEXT_ALL, NS_CONTEXT_GLOBAL, isGlobalNamespace, isPackNamespace } from "@tubo/semantic-concepts";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";
import { publishStatusVariant } from "@client/components/semantic-platform/publish-status";
import { useUiTranslation } from "@tubo/ui/i18n";

export const Route = createFileRoute("/_app/semantic-platform/quantities/")({
  // 领域上下文入 URL（缺省=全局；__all__=全部领域聚合浏览）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: QuantityListPage,
});

const PAGE_SIZE = 20;

function QuantityListPage() {
  const { t, i18n } = useUiTranslation("semanticPlatform");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [keyword, setKeyword] = useState("");
  const [unitFilter, setUnitFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<QuantityKindListItemVO | null>(null);

  // 领域上下文（切换器与列表写同一 URL 参数）；聚合浏览模式下提供领域筛选
  const { ns, nsFilter, setNs } = useNsContext();
  const showNsFilter = ns === NS_CONTEXT_ALL;
  const unitFilterOptions = useMemo(
    () => [
      { value: "all", label: t("quantities.list.unitAll") },
      { value: "standardOnly", label: t("quantities.list.standardOnly") },
      { value: "withConversions", label: t("quantities.list.withConversions") },
    ],
    [t],
  );

  const { data, isLoading } = useQuantityKindList(undefined, { query: {} });
  const { data: namespaces = [] } = useSemanticNamespaceList({ query: {} });
  const nsNameById = useMemo(
    () => new Map(namespaces.map((ns) => [ns.id, ns.name])),
    [namespaces],
  );
  const globalNsIds = useMemo(
    () => new Set(namespaces.filter((nsItem) => isGlobalNamespace(nsItem.code)).map((nsItem) => nsItem.id)),
    [namespaces],
  );
  const nsFilterOptions = useMemo(
    () => [
      { value: NS_CONTEXT_ALL, label: t("quantities.list.allDomains") },
      { value: NS_CONTEXT_GLOBAL, label: t("quantities.list.global") },
      ...namespaces
        .filter((nsItem) => !isPackNamespace(nsItem.code) && !isGlobalNamespace(nsItem.code))
        .map((nsItem) => ({ value: nsItem.id, label: nsItem.name })),
    ],
    [namespaces, t],
  );

  useEffect(() => {
    setPage(1);
  }, [keyword, unitFilter, nsFilter]);

  const filteredData = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return (data ?? []).filter((quantity) => {
      const matchesKeyword = !normalizedKeyword ||
        [quantity.code, quantity.name, quantity.standardUnit].some((value) => value.toLowerCase().includes(normalizedKeyword));
      const matchesUnits = unitFilter === "all" ||
        (unitFilter === "standardOnly" ? quantity.unitCount === 1 : quantity.unitCount > 1);
      const matchesNs =
        nsFilter === NS_CONTEXT_ALL ||
        (nsFilter === NS_CONTEXT_GLOBAL
          ? !quantity.namespaceId || globalNsIds.has(quantity.namespaceId)
          : quantity.namespaceId === nsFilter);
      return matchesKeyword && matchesUnits && matchesNs;
    });
  }, [data, globalNsIds, keyword, unitFilter, nsFilter]);

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: getQuantityKindListQueryKey() });

  const createMutation = useQuantityKindCreate({
    mutation: {
      onSuccess: () => {
        invalidateList();
        toast.success(t("quantities.list.created"));
        setCreateOpen(false);
      },
      onError: () => toast.error(t("quantities.list.createFailed")),
    },
  });

  const deleteMutation = useQuantityKindDelete({
    mutation: {
      onSuccess: () => {
        invalidateList();
        toast.success(t("quantities.list.deleted"));
        setDeleteTarget(null);
      },
      onError: () => toast.error(t("quantities.list.deleteFailed")),
    },
  });

  const publishMutation = usePublishQuantityKind({
    mutation: {
      onSuccess: (detail) => {
        invalidateList();
        toast.success(t("quantities.list.published", { name: detail?.name ?? t("navigation.quantities") }));
      },
      onError: () => toast.error(t("quantities.list.publishFailed")),
    },
  });

  const columns: DataTableColumn<QuantityKindListItemVO>[] = [
    { id: "code", header: t("quantities.list.code"), cell: (r) => <span className="font-mono text-sm">{r.code}</span> },
    { id: "name", header: t("quantities.list.name"), cell: (r) => (<span className="font-medium">{r.name}{r.origin?.startsWith("pack:") && (<Badge variant="outline" className="ml-1.5 text-[10px]">{t("quantities.list.industryDemo")}</Badge>)}</span>) },
    { id: "standardUnit", header: t("quantities.list.standardUnit"), cell: (r) => <span className="font-mono text-sm">{r.standardUnit}</span> },
    {
      id: "namespace",
      header: t("quantities.list.domain"),
      cell: (r) =>
        r.namespaceId ? (
          <Badge variant="outline">{r.namespaceName ?? nsNameById.get(r.namespaceId) ?? r.namespaceId}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">{t("quantities.list.global")}</span>
        ),
    },
    {
      id: "status",
      header: t("quantities.list.status"),
      cell: (r) => <Badge variant={publishStatusVariant(r.status)}>{r.status === "draft" ? t("quantities.statusDraft") : r.status === "pendingReview" ? t("quantities.statusPendingReview") : r.status === "published" ? t("quantities.statusPublished") : r.status === "deprecated" ? t("quantities.statusDeprecated") : r.status}</Badge>,
    },
    { id: "unitCount", header: t("quantities.list.unitCount"), cell: (r) => r.unitCount },
    { id: "updatedAt", header: t("quantities.list.updatedAt"), cell: (r) => new Date(r.updatedAt).toLocaleString(i18n.resolvedLanguage) },
    {
      id: "actions",
      header: t("quantities.list.actions"),
      cell: (r) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("quantities.list.edit")}
            title={t("quantities.list.edit")}
            onClick={() => navigate({ to: "/semantic-platform/quantities/$id", params: { id: r.id }, search: ns ? { ns } : {} })}
          >
            <Pencil className="size-3.5" />
          </Button>
          {r.status !== "published" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={publishMutation.isPending}
              title={t("quantities.list.publishTitle")}
              onClick={() => publishMutation.mutate({ id: r.id })}
            >
              {t("quantities.list.publish")}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("quantities.list.deleteLabel")}
            title={t("quantities.list.deleteLabel")}
            className="text-destructive"
            onClick={() => setDeleteTarget(r)}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  const total = filteredData.length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(total / pageSize)));
  const pagedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">{t("navigation.quantities")}</span>,
    [t],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {showNsFilter && (
          <Select items={nsFilterOptions} value={nsFilter} onValueChange={(value) => setNs(value ?? NS_CONTEXT_ALL)}>
            <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{nsFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select items={unitFilterOptions} value={unitFilter} onValueChange={(value) => setUnitFilter(value ?? "all")}>
          <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{unitFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="w-56 pl-8" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder={t("quantities.list.searchPlaceholder")} />
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" /> {t("quantities.list.create")}
        </Button>
      </div>
    ),
    [keyword, unitFilter, unitFilterOptions, nsFilter, nsFilterOptions, showNsFilter, setNs, t],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="h-full min-h-0 min-w-0">
      <DataTable
        data={pagedData}
        columns={columns}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        page={currentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={total}
        onPageChange={setPage}
        onRowClick={(row) => navigate({ to: "/semantic-platform/quantities/$id", params: { id: row.id }, search: ns ? { ns } : {} })}
      />

      <QuantityCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultNamespaceId={nsFilter !== NS_CONTEXT_ALL && nsFilter !== NS_CONTEXT_GLOBAL ? nsFilter : undefined}
        onSubmit={(payload) => createMutation.mutate({ data: payload })}
        submitting={createMutation.isPending}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("quantities.list.deleteTitle", { name: deleteTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("quantities.list.deleteDescription", { code: deleteTarget?.code })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("quantities.list.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate({ id: deleteTarget.id })}
            >
              {t("quantities.list.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QuantityCreateDialog({
  open,
  onOpenChange,
  defaultNamespaceId,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 创建默认归属（当前领域上下文；缺省落全局） */
  defaultNamespaceId?: string;
  onSubmit: (payload: { code: string; name: string; standardUnit: string; desc?: string; namespaceId?: string; units: UnitDefinitionVO[] }) => void;
  submitting: boolean;
}) {
  const { t } = useUiTranslation("semanticPlatform");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [standardUnit, setStandardUnit] = useState("");
  const [desc, setDesc] = useState("");
  const [namespaceId, setNamespaceId] = useState<string>(NS_CONTEXT_GLOBAL);
  const [extra, setExtra] = useState<UnitDefinitionVO>({ symbol: "", name: "", conversionType: "linear" });

  const { data: namespaces = [] } = useSemanticNamespaceList({ query: {} });
  const nsOptions = useMemo(
    () => [
      { value: NS_CONTEXT_GLOBAL, label: t("quantities.list.global") },
      ...namespaces
        .filter((ns) => !isPackNamespace(ns.code) && !isGlobalNamespace(ns.code))
        .map((ns) => ({ value: ns.id, label: ns.name })),
    ],
    [namespaces, t],
  );

  // 打开时预填当前领域上下文
  useEffect(() => {
    if (open) setNamespaceId(defaultNamespaceId ?? NS_CONTEXT_GLOBAL);
  }, [open, defaultNamespaceId]);

  const patchExtra = (patch: Partial<UnitDefinitionVO>) => setExtra((prev) => ({ ...prev, ...patch }));

  // 换算规则按类型校验:linear 必填因子;expression 必填表达式;piecewise 至少一段
  const extraType = extra.conversionType ?? "linear";
  const extraRuleValid =
    extraType === "expression" ? (extra.expression ?? "").trim() !== ""
    : extraType === "piecewise" ? (extra.segments ?? []).length > 0
    : extra.convertFactor !== undefined;
  const hasExtra = extra.symbol.trim() !== "";
  const valid = code.trim() && name.trim() && standardUnit.trim() && (!hasExtra || extraRuleValid);

  const units: UnitDefinitionVO[] = [
    { symbol: standardUnit.trim(), name: standardUnit.trim() },
    ...(hasExtra ? [{ ...extra, symbol: extra.symbol.trim(), name: extra.symbol.trim() }] : []),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("quantities.list.createTitle")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>{t("quantities.list.requiredCode")}</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("quantities.list.codePlaceholder")} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("quantities.list.requiredName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("quantities.list.namePlaceholder")} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("quantities.list.standardUnitRequired")}</Label>
            <Input value={standardUnit} onChange={(e) => setStandardUnit(e.target.value)} placeholder={t("quantities.list.standardUnitPlaceholder")} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("quantities.list.description")}</Label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("quantities.list.descriptionPlaceholder")} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("quantities.list.domain")}</Label>
            <Select items={nsOptions} value={namespaceId} onValueChange={(v) => setNamespaceId(v ?? NS_CONTEXT_GLOBAL)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {nsOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("quantities.list.conversionHint")}
          </p>
          <div className="grid gap-1.5">
            <Label>{t("quantities.list.unitSymbol")}</Label>
            <Input value={extra.symbol} onChange={(e) => patchExtra({ symbol: e.target.value })} placeholder="°C/s" />
          </div>
          {hasExtra && (
            <div className="grid gap-1.5">
              <Label>{t("quantities.list.conversionRule")}</Label>
              <UnitConversionFields unit={extra} onPatch={patchExtra} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>{t("quantities.list.cancel")}</Button>
          <Button size="sm" disabled={!valid || submitting} onClick={() => onSubmit({
            code: code.trim(),
            name: name.trim(),
            standardUnit: standardUnit.trim(),
            ...(desc.trim() ? { desc: desc.trim() } : {}),
            ...(namespaceId !== NS_CONTEXT_GLOBAL ? { namespaceId } : {}),
            units,
          })}>
            {t("quantities.list.createAction")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
